// src/pages/form/FormTemplatesPage.tsx
// Form templates — list, create, install (creates a draft form and opens the
// builder). See docs/ux/form-ia.md §3.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useForm, useI18n } from '@react/hooks';
import { Alert, Badge, Button, Input, Textarea } from '@react/components';
import { useScope } from '@/hooks/useScope';
import type { FormTemplate } from '@core/types/form';
import { FormModalShell } from '@/features/form/FormModalShell';
import { errMessage, makeEntity } from '@/features/form/helpers';

export const FormTemplatesPage = observer(() => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const store = useForm();

  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [viewing, setViewing] = useState<FormTemplate | null>(null);

  const load = () => {
    setIsLoading(true);
    setError(null);
    store
      .ListFormTemplates()
      .then((res) => setTemplates(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('form.templates.loadError'))))
      .finally(() => setIsLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await store.CreateFormTemplate({
        entity: makeEntity('', 'form_template', scope),
        name: name.trim(),
        description: description || undefined,
        category: category || undefined,
        form_schema: JSON.stringify({ pages: [] }),
        is_public: false,
      });
      setDialogOpen(false);
      setName('');
      setDescription('');
      setCategory('');
      load();
    } catch (e) {
      setError(errMessage(e, t('form.templates.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const view = async (templateId: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await store.GetFormTemplate(templateId);
      setViewing(res.template ?? null);
    } catch (e) {
      setError(errMessage(e, t('form.templates.loadError')));
    } finally {
      setBusy(false);
    }
  };

  const install = async (templateId: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await store.InstallFormTemplate(templateId, {});
      if (res.form?.entity?.id) navigate(`/forms/${res.form.entity.id}`);
    } catch (e) {
      setError(errMessage(e, t('form.templates.installError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('form.templates.title')}</h1>
        <Button size="sm" className="ml-auto" onClick={() => setDialogOpen(true)}>{t('form.templates.newTemplate')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {isLoading && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {[0, 1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-md bg-muted" />)}
        </div>
      )}

      {!isLoading && templates.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('form.templates.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('form.templates.subtitle')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((tpl) => (
          <div key={tpl.entity?.id} className="flex flex-col justify-between rounded-md border border-border p-4">
            <div>
              <div className="flex items-center gap-2">
                <button type="button" className="font-medium text-foreground hover:underline" onClick={() => view(tpl.entity?.id ?? '')}>
                  {tpl.name}
                </button>
                {tpl.is_public && <Badge variant="secondary">{t('form.templates.public')}</Badge>}
              </div>
              {tpl.category && <p className="mt-1 text-xs text-muted-foreground">{tpl.category}</p>}
              {tpl.description && <p className="mt-1 text-sm text-muted-foreground">{tpl.description}</p>}
              <p className="mt-2 text-xs text-muted-foreground">{t('form.templates.installCount', { count: tpl.install_count })}</p>
            </div>
            <Button size="sm" className="mt-3" isLoading={busy} onClick={() => install(tpl.entity?.id ?? '')}>
              {t('form.templates.install')}
            </Button>
          </div>
        ))}
      </div>

      <FormModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t('form.templates.newTemplate')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={busy}>{t('common.cancel')}</Button>
            <Button size="sm" onClick={create} isLoading={busy} disabled={!name.trim()}>{t('common.create')}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Input label={t('form.templates.category')} value={category} onChange={(e) => setCategory(e.target.value)} />
          <Textarea label={t('common.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </div>
      </FormModalShell>

      <FormModalShell open={!!viewing} onClose={() => setViewing(null)} title={viewing?.name ?? ''}>
        {viewing && (
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">{viewing.description || t('form.templates.noDescription')}</p>
            <p className="text-xs text-muted-foreground">{t('form.templates.usageCount', { count: viewing.usage_count })}</p>
            <pre className="max-h-64 overflow-auto rounded-md bg-muted p-2 text-xs text-foreground">{viewing.form_schema}</pre>
          </div>
        )}
      </FormModalShell>
    </div>
  );
});
